import React, { useCallback, useContext, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  ChatStates,
  selectChatState,
  selectTextChats,
  setScoreOverviewVisible,
  setUserEndPromptActive,
} from '@vh/slices/chat/ChatSlice';
import VHOverlayForCall from './VHOverlayForCall';
import {
  courseIDSelector,
  nextUnitSelector,
  virtualHumanDataSelector,
} from '@vh/slices/VirtualHumanSlice';
import {
  isEmbeddedPlayerSelector,
  isFullscreenSelector,
  setMoreInfoVisible,
} from '@vh/slices/vh-player/VHPlayerSlice';
import { patientSelector } from '@vh/slices/PatientSlice';
import {
  loadingBotResponseSelector,
  loadingUserRequestSelector,
} from '@vh/slices/CallSlice';
import { useVHTypeConfig } from '@vh/components/vh-types/vhTypeHooks';
import FinishSessionButton from '@vh/components/FinishSessionButton/FinishSessionButton';
import UnitCompleteNext from '@vh/components/UnitCompleteNext/UnitCompleteNext';
import { Button } from '@virtidev/toolbox';
import { EndSessionButtons } from '@vh/components/VHOverlays/VHOverlayForCall/VHOverlayForCall.styled';
import CallTextInput from '@vh/components/VHOverlays/VHOverlayForCall/components/CallTextInput/CallTextInput';
import { RealtimeOpenAIContext } from '@vh/components/VHPlayer/RealtimeOpenAIPlayer/RealtimeOpenAI.context';
import { MicrophoneStreamContext } from '@core/components/MicrophoneStreamProvider/MicrophoneStreamProvider';

/** @type {React.FC<{fullscreenElementRef: React.MutableRefObject<HTMLDivElement | null>}>} */
const VHOverlayForCallPopulator = ({ fullscreenElementRef }) => {
  const textChats = useSelector(selectTextChats);
  const { TimeLimit } = useSelector(virtualHumanDataSelector);
  const vh = useSelector(virtualHumanDataSelector);
  const vhDBVH = useSelector(patientSelector);
  const dispatch = useDispatch();

  const { stream } = useContext(MicrophoneStreamContext);

  const handleSeeMoreClick = useCallback(() => {
    dispatch(setMoreInfoVisible(true));
  }, [dispatch]);

  const vhTypeConfig = useVHTypeConfig();

  const handleTimerComplete = useCallback(() => {
    vhTypeConfig.onFinishSession(dispatch, vhDBVH);
  }, [dispatch, vhDBVH, vhTypeConfig]);

  const handleHangUp = useCallback(() => {
    dispatch(setUserEndPromptActive(true));
  }, [dispatch]);

  const loadingBotResponse = useSelector(loadingBotResponseSelector);
  const loadingUserRequest = useSelector(loadingUserRequestSelector);
  const nextUnit = useSelector(nextUnitSelector);
  const courseID = useSelector(courseIDSelector);

  const handleShowScoreModal = useCallback(() => {
    dispatch(setScoreOverviewVisible(true));
  }, [dispatch]);

  const chatState = useSelector(selectChatState);

  const [textVal, setTextVal] = useState('');
  const handleChange = useCallback((e) => {
    setTextVal(e.target.value);
  }, []);

  const textInputRef = useRef(null);
  const isPublicPlayer = useSelector(isEmbeddedPlayerSelector);

  const { connected, sendText, audioStream } = useContext(
    RealtimeOpenAIContext
  );
  const handleSubmitText = useCallback(() => {
    if (!textVal) return;
    sendText(textVal);
    setTextVal('');
  }, [sendText, textVal]);

  const [muted, setMuted] = useState(false);
  const { setVolume } = useContext(MicrophoneStreamContext);
  const handleMuteChange = useCallback(
    (newMuted) => {
      setMuted(newMuted);
      setVolume(newMuted ? 0 : 1);
    },
    [setVolume]
  );

  const isFullscreen = useSelector(isFullscreenSelector);

  return (
    <VHOverlayForCall
      muted={muted}
      onMuteChange={handleMuteChange}
      fullscreenElementRef={fullscreenElementRef}
      totalTimeInSeconds={TimeLimit * 60}
      textChats={textChats}
      // avatarSrc={vh?.ThumbnailURL}
      description={vh?.Description}
      title={vh?.Title}
      onSeeMoreClick={handleSeeMoreClick}
      vhName={vhDBVH?.name}
      onTimerComplete={handleTimerComplete}
      onHangUp={handleHangUp}
      disabled={chatState !== ChatStates.TALK || !connected}
      loadingBotResponse={loadingBotResponse}
      loadingUserRequest={loadingUserRequest}
      audioStream={audioStream ?? undefined}
      micStream={stream ?? undefined}
      isPublicPlayer={isPublicPlayer}
      isFullscreen={isFullscreen}
      logsFooter={
        <>
          {chatState !== ChatStates.COMPLETE && (
            <CallTextInput
              placeholder="Start typing..."
              value={textVal}
              onChange={handleChange}
              onSubmit={handleSubmitText}
              ref={textInputRef}
              disabled={chatState !== ChatStates.TALK || !connected}
              onEnter={handleSubmitText}
            />
          )}
          {chatState === ChatStates.COMPLETE && (
            <EndSessionButtons>
              <Button
                design="outline"
                color="turquoise"
                onClick={handleShowScoreModal}
              >
                Show score summary
              </Button>
              {!nextUnit && <FinishSessionButton courseID={courseID} />}
              {nextUnit && (
                <UnitCompleteNext nextUnit={nextUnit} courseID={courseID} />
              )}
            </EndSessionButtons>
          )}
        </>
      }
    />
  );
};

export default VHOverlayForCallPopulator;
